import { useState } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db6c4mjghk82orf0ck40";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢你的反馈，我们已经收到！");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const str = String(value);
      if (str.trim() === "") return;
      params.append(key, str);
    });

    setStatus("submitting");
    setFeedback("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback(parsed?.meta?.message || "提交成功，感谢你的反馈！");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg || "提交失败，请检查填写内容后重试");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg text-foreground-950 mb-1 flex items-center gap-2">
        <i className="ri-chat-smile-2-line text-accent-600" />
        意见反馈
      </h3>
      <p className="text-sm text-foreground-500 mb-5">
        对直播体验、赛程信息或资讯内容有任何建议，欢迎留言告诉我们。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入称呼"
            className="w-full bg-background-100 border border-background-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full bg-background-100 border border-background-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-topic" className="block text-sm text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="fb-topic"
          name="topic"
          className="w-full bg-background-100 border border-background-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="直播体验">直播体验</option>
          <option value="赛程信息">赛程信息</option>
          <option value="资讯内容">资讯内容</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="block text-sm text-foreground-700 mb-1.5">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或遇到的问题（最多 500 字）"
          className="w-full bg-background-100 border border-background-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="aux-check-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {feedback && (
        <div
          className={`mt-4 flex items-start gap-2 px-4 py-3 rounded-md text-sm ${
            status === "success"
              ? "bg-accent-50 text-accent-800 border border-accent-200"
              : "bg-secondary-100 text-secondary-900 border border-secondary-200"
          }`}
        >
          <i
            className={
              status === "success" ? "ri-checkbox-circle-line mt-0.5" : "ri-error-warning-line mt-0.5"
            }
          />
          <span>{feedback}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}